import {
    IonContent,
    IonHeader,
    IonPage,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonMenuButton
} from '@ionic/react';
import React from 'react';
import { useAuth } from '../context/AuthContext.tsx';
import { auth, user as userApi } from '../services/api.ts';
import './Agent.css';

const Agent: React.FC = () => {
    const { user, loading, getAccessToken } = useAuth();
    const [id, setId] = React.useState<string | null>(null);
    const [token, setToken] = React.useState<string | null>(null);

    React.useEffect(() => {
        const fetchTokenAndId = async () => {
            // Try Supabase JWT token first
            let accessToken = await getAccessToken();

            // Fallback: check for backend-issued token (from email/password login)
            if (!accessToken) {
                try {
                    const backendToken = localStorage.getItem('authToken');
                    if (backendToken) {
                        accessToken = backendToken;
                        console.log("Using backend auth token for agent");
                    }
                } catch (e) {
                    // ignore localStorage errors
                }
            }

            if (accessToken) {
                setToken(accessToken);
            }

            if (user?.id) {
                setId(user.id);
            } else if (auth.isAuthenticated()) {
                // Fetch internal profile ID as fallback
                try {
                    const profile = await userApi.getProfile();
                    if (profile?.id) setId(profile.id);
                } catch (err) {
                    console.error("Failed to fetch profile for agent:", err);
                }
            }
        };

        fetchTokenAndId();
    }, [user, getAccessToken]);

    // Use custom interviewer UI served from job-agents project
    const AGENT_BASE_URL = process.env.REACT_APP_ADK_URL || "http://localhost:8501";

    // Frontend passes JWT token for authentication
    const agentUrl = React.useMemo(() => {
        if (!token) return null;  // Don't load iframe until we have token
        const url = `${AGENT_BASE_URL}?token=${encodeURIComponent(token)}`;
        console.log("Using Agent URL (token hidden):", `${AGENT_BASE_URL}?token=***`);
        return url;
    }, [AGENT_BASE_URL, token]);

    const isAuthenticated = !!user || auth.isAuthenticated();

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonMenuButton />
                    </IonButtons>
                    <IonTitle>Mock Interview</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent className="agent-content" scrollY={false}>
                {loading ? (
                    <div className="login-prompt">
                        <p>Loading session...</p>
                    </div>
                ) : !isAuthenticated ? (
                    <div className="login-prompt">
                        <p>Please log in to practice with the Voice Agent.</p>
                    </div>
                ) : !agentUrl ? (
                    <div className="login-prompt">
                        <p>Authenticating...</p>
                    </div>
                ) : (
                    <iframe
                        src={agentUrl}
                        title="Voice Agent"
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            borderRadius: '0'
                        }}
                        allow="microphone; camera; display-capture; autoplay; clipboard-read; clipboard-write; encrypted-media"
                    />
                )}
            </IonContent>
        </IonPage>
    );
};

export default Agent;
